/**
 * Authentication service for authenticated users
 *
 * USER AUTH:
 * - Sign in with Google OAuth
 * - Sign in with Microsoft OAuth (Azure AD)
 * - Sign in with Email/Password
 * - Sign up, sign out, password reset
 * - Full user identity and session management
 */

import { supabase } from './supabase';
import type { User, Session, AuthError } from '@supabase/supabase-js';

// ============================================================================
// SESSION & USER INFO FUNCTIONS
// ============================================================================

/**
 * Get the current user session (if signed in)
 */
export async function getSession(): Promise<Session | null> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session;
  } catch (error) {
    return null;
  }
}

/**
 * Get the current user info (if signed in)
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return user;
  } catch (error) {
    return null;
  }
}

// Token cache to avoid redundant Supabase calls
let cachedToken: string | null = null;
let tokenExpiry: number | null = null;

/**
 * Get an authentication token for API calls
 * Returns the user's token if signed in, throws error if not authenticated
 *
 * Always calls getSession() to let Supabase auto-refresh expired tokens.
 * If the token appears expired, explicitly tries to refresh the session.
 * The cache is only used as a short-term optimization to avoid excessive calls.
 */
export async function getAuthToken(): Promise<string> {
  // Always get fresh session - this allows Supabase to auto-refresh expired tokens
  // getSession() will automatically refresh expired tokens if autoRefreshToken is enabled
  const {
    data: { session: initialSession },
    error: sessionError,
  } = await supabase.auth.getSession();

  let session = initialSession;

  // If we got a session but the token might be expired, try to refresh explicitly
  if (session?.access_token && !sessionError) {
    try {
      // Check if token is expired by trying to decode it (basic check)
      // If it's expired, try to refresh the session
      const tokenParts = session.access_token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const now = Math.floor(Date.now() / 1000);
        // If token is expired or expires within 5 minutes, try to refresh
        if (payload.exp && payload.exp <= now + 300) {
          const { data: refreshData, error: refreshError } =
            await supabase.auth.refreshSession();
          if (!refreshError && refreshData?.session) {
            session = refreshData.session;
          }
        }
      }
    } catch (e) {
      // If we can't decode the token, just use what we have
      // Supabase will handle validation
    }
  }

  if (sessionError || !session?.access_token) {
    // Clear cache on error
    cachedToken = null;
    tokenExpiry = null;
    throw new Error('No valid session found. Please sign in again.');
  }

  // Use the fresh token from session (Supabase handles refresh automatically)
  // Only use cache if it's the same token and we're not close to expiry
  const now = Date.now();
  const expiresIn = session.expires_in || 3600; // Default to 1 hour if not provided
  const tokenExpiresAt = now + expiresIn * 1000;
  const fiveMinutesFromNow = now + 5 * 60 * 1000;

  // If cached token matches and we're not within 5 minutes of expiry, use cache
  // Otherwise, always use the fresh token from getSession()
  if (
    cachedToken === session.access_token &&
    tokenExpiry &&
    tokenExpiresAt > fiveMinutesFromNow
  ) {
    return cachedToken;
  }

  // Update cache with fresh token
  cachedToken = session.access_token;
  tokenExpiry = tokenExpiresAt;

  return session.access_token;
}

/**
 * Clear the cached auth token
 * Call this when user signs out or session is invalidated
 */
export function clearAuthTokenCache(): void {
  cachedToken = null;
  tokenExpiry = null;
}

// ============================================================================
// AUTH FUNCTIONS (Sign In/Up/Out)
// ============================================================================

/**
 * Sign in with email and password
 */
export async function signInWithPassword(
  email: string,
  password: string
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { user: null, error };
    }

    return { user: data.user, error: null };
  } catch (error) {
    return {
      user: null,
      error: error as AuthError,
    };
  }
}

/**
 * Sign up with email and password
 */
export async function signUpWithPassword(
  email: string,
  password: string,
  options?: { fullName?: string }
): Promise<{
  user: User | null;
  error: AuthError | null;
  session: Session | null;
}> {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: options?.fullName,
        },
      },
    });

    if (error) {
      return { user: null, error, session: null };
    }

    return { user: data.user, error: null, session: data.session };
  } catch (error) {
    return {
      user: null,
      error: error as AuthError,
      session: null,
    };
  }
}

/**
 * Sign in with Google OAuth
 * Redirects to dashboard after authentication
 */
export async function signInWithGoogle(redirectTo?: string): Promise<{
  error: AuthError | null;
}> {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // Redirect to specified URL or dashboard after authentication
        redirectTo: redirectTo || `${window.location.origin}/app/dashboard`,
      },
    });

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (error) {
    return { error: error as AuthError };
  }
}

/**
 * Sign in with Microsoft OAuth (Azure AD)
 * Redirects to dashboard after authentication
 */
export async function signInWithMicrosoft(redirectTo?: string): Promise<{
  error: AuthError | null;
}> {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        // Redirect to specified URL or dashboard after authentication
        redirectTo: redirectTo || `${window.location.origin}/app/dashboard`,
        scopes: 'email',
      },
    });

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (error) {
    return { error: error as AuthError };
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  try {
    // Clear cached token on sign out
    clearAuthTokenCache();

    // Sign out with scope: 'local' to prevent Google OAuth redirect
    // This only clears the local session without redirecting to Google
    const { error } = await supabase.auth.signOut({ scope: 'local' });

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (error) {
    return { error: error as AuthError };
  }
}

/**
 * Send a password reset email
 */
export async function resetPassword(
  email: string
): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (error) {
    return { error: error as AuthError };
  }
}

// ============================================================================
// IDENTITY & PASSWORD MANAGEMENT
// ============================================================================

export interface UserIdentity {
  id: string;
  provider: string;
  identity_id: string;
  created_at: string;
  updated_at: string;
  identity_data?: Record<string, unknown>;
}

/**
 * Get all linked identities for the current user.
 * Each identity has a `provider` field: "email", "google", "azure", etc.
 */
export async function getUserIdentities(): Promise<{
  identities: UserIdentity[];
  error: AuthError | null;
}> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return { identities: [], error: null };
    }

    return {
      identities: (user.identities ?? []) as UserIdentity[],
      error: null,
    };
  } catch (error) {
    return { identities: [], error: error as AuthError };
  }
}

/**
 * Link an additional OAuth provider to the current user's account.
 * Requires "Enable Manual Linking" in Supabase Dashboard.
 */
export async function linkIdentity(
  provider: 'google' | 'azure'
): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo: `${window.location.origin}/app/profile`,
        ...(provider === 'azure' ? { scopes: 'email' } : {}),
      },
    });

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (error) {
    return { error: error as AuthError };
  }
}

/**
 * Update the current user's password.
 * Works for both setting an initial password (OAuth user adding email auth)
 * and changing an existing password.
 */
export async function updatePassword(
  newPassword: string
): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (error) {
    return { error: error as AuthError };
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const user = await getCurrentUser();
  return user !== null;
}

/**
 * Listen to auth state changes
 * Callback receives user or null
 */
export function onAuthStateChange(
  callback: (user: User | null) => void
): () => void {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user || null);
  });

  // Return unsubscribe function
  return () => {
    subscription.unsubscribe();
  };
}
